/*
  Dhrohar wishlist (browser-only, no account needed).

  Storage: localStorage['dhrohar:wishlist:v1'] = [{ handle, variantId, productId, title, variantTitle,
  url, image, addedAt }]. Every storage access is wrapped in try/catch; if storage is missing, blocked,
  full or corrupt the list lives in memory for this page view. Nothing here throws.

  Public API: window.DhroharWishlist = { key, items(), count(), has(handle, variantId?), add(item),
  remove(handle, variantId?), toggle(item, scope?), clear(), isPersistent() }.
  Event: 'dhrohar:wishlist:change' on document, detail { items, action, item }.
  Elements:
    <dhrohar-wishlist-button data-product-handle data-product-id data-variant-id data-product-title
      data-variant-title data-product-url data-product-image [data-scope="product|variant"]
      [data-show-label]>  heart toggle (aria-pressed); follows Dawn variant changes on the product page.
    <dhrohar-wishlist-count>  number of saved items (hidden at 0).
    <dhrohar-wishlist-page data-money-format ...>  wishlist page (sections/dhrohar-wishlist.liquid).
  Safe to load more than once; elements are safe to re-render on section reload.
*/
(() => {
  const root = typeof window !== 'undefined' ? window : globalThis;
  if (root.DhroharWishlist) return;

  const KEY = 'dhrohar:wishlist:v1';
  const EVENT = 'dhrohar:wishlist:change';
  const MAX_ITEMS = 100;
  const doc = root.document;

  /* ---------- store ---------- */
  let memory = [];
  let persistent = true; // false after any storage failure: memory becomes the source of truth

  const str = (v) => (v === undefined || v === null ? '' : String(v));

  function sanitize(list) {
    if (!Array.isArray(list)) return [];
    const out = [];
    const seen = new Set();
    for (const raw of list) {
      if (!raw || typeof raw !== 'object' || typeof raw.handle !== 'string' || !raw.handle) continue;
      const item = {
        handle: raw.handle,
        variantId: str(raw.variantId),
        productId: str(raw.productId),
        title: str(raw.title),
        variantTitle: str(raw.variantTitle),
        url: str(raw.url),
        image: str(raw.image),
        addedAt: Number(raw.addedAt) || 0,
      };
      const id = item.handle + '|' + item.variantId;
      if (seen.has(id)) continue;
      seen.add(id);
      out.push(item);
      if (out.length >= MAX_ITEMS) break;
    }
    return out;
  }

  function getStorage() {
    try {
      const s = root.localStorage;
      return s && typeof s.getItem === 'function' ? s : null;
    } catch (e) {
      return null;
    }
  }

  function read() {
    if (persistent) {
      try {
        const s = getStorage();
        if (!s) throw new Error('no storage');
        const raw = s.getItem(KEY);
        memory = raw ? sanitize(JSON.parse(raw)) : []; // corrupt JSON → memory-only for this page view
      } catch (e) {
        persistent = false;
      }
    }
    return memory.map((i) => ({ ...i }));
  }

  function write(list) {
    memory = sanitize(list);
    if (persistent) {
      try {
        const s = getStorage();
        if (!s) throw new Error('no storage');
        s.setItem(KEY, JSON.stringify(memory));
      } catch (e) {
        persistent = false;
      }
    }
    return memory.map((i) => ({ ...i }));
  }

  const same = (item, handle, variantId) =>
    item.handle === handle && (variantId === undefined || variantId === null || variantId === '' || item.variantId === str(variantId));

  function emit(action, item) {
    const items = memory.map((i) => ({ ...i }));
    try {
      if (doc && typeof doc.dispatchEvent === 'function' && typeof root.CustomEvent === 'function') {
        doc.dispatchEvent(new root.CustomEvent(EVENT, { detail: { items, action, item } }));
      }
    } catch (e) {
      /* a listener failed; the wishlist itself is fine */
    }
  }

  const api = {
    key: KEY,
    items: () => read(),
    count: () => read().length,
    has(handle, variantId) {
      return read().some((i) => same(i, str(handle), variantId));
    },
    add(item) {
      try {
        const clean = sanitize([{ ...item, addedAt: Date.now() }])[0];
        if (!clean) return false;
        const list = read().filter((i) => !same(i, clean.handle, clean.variantId));
        list.unshift(clean);
        write(list);
        emit('add', clean);
        return true;
      } catch (e) {
        return false;
      }
    },
    remove(handle, variantId) {
      try {
        const list = read();
        const removed = list.filter((i) => same(i, str(handle), variantId));
        if (!removed.length) return false;
        write(list.filter((i) => !same(i, str(handle), variantId)));
        emit('remove', removed[0]);
        return true;
      } catch (e) {
        return false;
      }
    },
    /* scope 'product': matches any saved variant of the product */
    toggle(item, scope) {
      const variantId = scope === 'product' ? undefined : item && item.variantId;
      if (item && api.has(item.handle, variantId)) {
        api.remove(item.handle, variantId);
        return false;
      }
      api.add(item);
      return true;
    },
    clear() {
      write([]);
      emit('clear', null);
    },
    isPersistent: () => {
      read();
      return persistent;
    },
  };
  root.DhroharWishlist = api;

  /* Other tabs: re-read and re-render. */
  try {
    root.addEventListener('storage', (event) => {
      if (event.key !== null && event.key !== KEY) return;
      persistent = true;
      read();
      emit('sync', null);
    });
  } catch (e) {
    /* no window events (tests) */
  }

  /* ---------- DOM helpers ---------- */
  if (!doc || !root.customElements || typeof root.HTMLElement !== 'function') return;

  /* Dawn's constants.js / pubsub.js declare PUB_SUB_EVENTS (const), subscribe and publish as global
     lexical bindings, so read them by name behind typeof guards (absent in tests or other themes). */
  function dawnPubSub() {
    try {
      /* global PUB_SUB_EVENTS, subscribe, publish */
      if (typeof PUB_SUB_EVENTS === 'undefined' || !PUB_SUB_EVENTS) return null;
      return {
        events: PUB_SUB_EVENTS,
        subscribe: typeof subscribe === 'function' ? subscribe : null,
        publish: typeof publish === 'function' ? publish : null,
      };
    } catch (e) {
      return null;
    }
  }

  let liveRegion = null;
  function announce(message) {
    try {
      if (!liveRegion || !liveRegion.isConnected) {
        liveRegion = doc.createElement('div');
        liveRegion.className = 'visually-hidden';
        liveRegion.setAttribute('role', 'status');
        liveRegion.setAttribute('aria-live', 'polite');
        doc.body.appendChild(liveRegion);
      }
      liveRegion.textContent = '';
      setTimeout(() => {
        liveRegion.textContent = message;
      }, 60);
    } catch (e) {
      /* ignore */
    }
  }

  const HEART =
    '<svg class="dhrohar-wish__icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 20s-7.5-4.6-7.5-10.1A4.2 4.2 0 0 1 12 7.4a4.2 4.2 0 0 1 7.5 2.5C19.5 15.4 12 20 12 20Z"/></svg>';

  /* ---------- <dhrohar-wishlist-button> ---------- */
  class DhroharWishlistButton extends root.HTMLElement {
    connectedCallback() {
      try {
        this.button = this.querySelector('button');
        if (!this.button) {
          this.button = doc.createElement('button');
          this.button.type = 'button';
          this.button.className = 'dhrohar-wish';
          this.button.innerHTML = HEART + (this.hasAttribute('data-show-label') ? '<span class="dhrohar-wish__label"></span>' : '');
          this.appendChild(this.button);
        }
        this.onClick = this.onClick.bind(this);
        this.refresh = this.refresh.bind(this);
        this.onVariantInput = this.onVariantInput.bind(this);
        this.button.addEventListener('click', this.onClick);
        doc.addEventListener(EVENT, this.refresh);
        // Only product-page buttons follow variant changes (cards change variant via swatches).
        this.productScope = this.closest('product-info, [data-section]');
        if (this.productScope || this.dataset.sectionId) {
          doc.addEventListener('change', this.onVariantInput);
          if (!this.subscribeVariant() && doc.readyState === 'loading') {
            doc.addEventListener('DOMContentLoaded', () => this.isConnected && this.subscribeVariant(), { once: true });
          }
        }
        this.refresh();
      } catch (e) {
        /* leave the static button in place */
      }
    }

    subscribeVariant() {
      if (this.unsubscribe) return true;
      const ps = dawnPubSub();
      if (!ps || !ps.subscribe || !ps.events.variantChange) return false;
      this.unsubscribe = ps.subscribe(ps.events.variantChange, (event) => this.onVariantChange(event));
      return true;
    }

    disconnectedCallback() {
      try {
        if (this.button) this.button.removeEventListener('click', this.onClick);
        doc.removeEventListener(EVENT, this.refresh);
        doc.removeEventListener('change', this.onVariantInput);
        if (typeof this.unsubscribe === 'function') this.unsubscribe();
        this.unsubscribe = null;
      } catch (e) {
        /* ignore */
      }
    }

    get item() {
      const d = this.dataset;
      return {
        handle: d.productHandle || '',
        variantId: d.variantId || '',
        productId: d.productId || '',
        title: d.productTitle || '',
        variantTitle: d.variantTitle || '',
        url: d.productUrl || '',
        image: d.productImage || '',
      };
    }

    get scope() {
      return this.dataset.scope === 'product' ? 'product' : 'variant';
    }

    get label() {
      const { title, variantTitle } = this.item;
      const showVariant = variantTitle && variantTitle !== 'Default Title' && this.scope === 'variant';
      return showVariant ? `${title} (${variantTitle})` : title;
    }

    isSaved() {
      const item = this.item;
      if (!item.handle) return false;
      return api.has(item.handle, this.scope === 'product' ? undefined : item.variantId);
    }

    refresh() {
      try {
        if (!this.button) return;
        const text = this.button.querySelector('.dhrohar-wish__label');
        if (this.unavailable) {
          // Product page: the chosen option combination does not exist, so there is nothing to save.
          this.button.setAttribute('aria-disabled', 'true');
          this.button.setAttribute('aria-pressed', 'false');
          this.button.setAttribute('aria-label', `Choose an available option to save ${this.item.title} to wishlist`);
          if (text) text.textContent = 'Save to wishlist';
          this.classList.remove('is-saved');
          this.classList.add('is-unavailable');
          return;
        }
        this.button.removeAttribute('aria-disabled');
        this.classList.remove('is-unavailable');
        const saved = this.isSaved();
        this.button.setAttribute('aria-pressed', String(saved));
        this.button.setAttribute('aria-label', saved ? `Remove ${this.label} from wishlist` : `Save ${this.label} to wishlist`);
        if (text) text.textContent = saved ? 'Saved to wishlist' : 'Save to wishlist';
        this.classList.toggle('is-saved', saved);
      } catch (e) {
        /* ignore */
      }
    }

    onClick(event) {
      event.preventDefault();
      try {
        if (this.unavailable || (this.scope === 'variant' && !this.dataset.variantId)) {
          announce('Choose an available option to save it to your wishlist');
          this.refresh();
          return;
        }
        const saved = api.toggle(this.item, this.scope);
        announce(saved ? `Saved to your wishlist: ${this.label}` : `Removed from your wishlist: ${this.label}`);
        this.refresh();
      } catch (e) {
        /* ignore */
      }
    }

    /* Dawn product page: PUB_SUB_EVENTS.variantChange → { data: { sectionId, variant } } */
    onVariantChange(event) {
      try {
        const data = (event && event.data) || {};
        const variant = data.variant;
        if (!variant || !variant.id) return;
        const scopeEl = this.closest('product-info, [data-section]');
        const sectionId = this.dataset.sectionId || (scopeEl && (scopeEl.dataset.section || scopeEl.dataset.sectionId));
        if (!sectionId || !data.sectionId || String(data.sectionId) !== String(sectionId)) return;
        this.setVariant(variant.id, variant.title, variant.featured_image && variant.featured_image.src);
      } catch (e) {
        /* ignore */
      }
    }

    /* Fallback: Dawn sets input[name="id"] and fires change on it */
    onVariantInput(event) {
      try {
        const target = event.target;
        if (!target || !target.matches) return;
        let id = '';
        if (target.matches('input[name="id"], select[name="id"]')) id = target.value;
        else if (target.closest('variant-selects')) {
          const container = target.closest('product-info, [data-section]');
          const input = container && container.querySelector('input[name="id"]');
          id = input ? input.value : '';
        } else return;
        const mine = this.closest('product-info, [data-section]');
        const theirs = target.closest('product-info, [data-section]');
        if (!mine || mine !== theirs) return;
        if (!id) {
          // Dawn clears the id (without publishing variantChange) for a combination that does not exist.
          this.unavailable = true;
          this.refresh();
          return;
        }
        if (id === this.dataset.variantId && !this.unavailable) return;
        // Full data for the newly selected variant from Dawn's [data-selected-variant] JSON when it matches.
        let variant = null;
        try {
          const json = mine.querySelector('variant-selects [data-selected-variant]');
          const parsed = json ? JSON.parse(json.textContent) : null;
          if (parsed && String(parsed.id) === String(id)) variant = parsed;
        } catch (e) {
          variant = null;
        }
        if (variant) this.setVariant(id, variant.title, variant.featured_image && variant.featured_image.src);
        else this.setVariant(id);
      } catch (e) {
        /* ignore */
      }
    }

    setVariant(id, title, image) {
      this.unavailable = false;
      this.dataset.variantId = String(id);
      const base = (this.dataset.productUrl || '').split('?')[0];
      if (base) this.dataset.productUrl = `${base}?variant=${id}`;
      if (typeof title === 'string') this.dataset.variantTitle = title === 'Default Title' ? '' : title;
      if (image) {
        try {
          const url = new URL(image, root.location.href);
          url.searchParams.set('width', '600');
          this.dataset.productImage = url.href.replace(/^https?:/, '');
        } catch (e) {
          this.dataset.productImage = image;
        }
      }
      this.refresh();
    }
  }

  /* ---------- <dhrohar-wishlist-count> ---------- */
  class DhroharWishlistCount extends root.HTMLElement {
    connectedCallback() {
      this.render = this.render.bind(this);
      doc.addEventListener(EVENT, this.render);
      this.render();
    }

    disconnectedCallback() {
      doc.removeEventListener(EVENT, this.render);
    }

    render() {
      try {
        const n = api.count();
        this.textContent = n ? String(n) : '';
        this.hidden = n === 0;
        this.dataset.count = String(n);
        const link = this.closest('a');
        if (link) {
          const base = link.dataset.dhroharLabel || link.getAttribute('aria-label') || 'Wishlist';
          if (!link.dataset.dhroharLabel) link.dataset.dhroharLabel = base;
          link.setAttribute('aria-label', n ? `${base}, ${n} saved` : base);
        }
      } catch (e) {
        /* ignore */
      }
    }
  }

  /* ---------- money ---------- */
  function formatMoney(cents, format) {
    const value = Number(cents) || 0;
    const fmt = str(format || '{{amount}}').replace(/<[^>]*>/g, '');
    const delimit = (amount, decimals, thousands, decimal) => {
      const fixed = (amount / 100).toFixed(decimals);
      const [whole, frac] = fixed.split('.');
      const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return frac ? grouped + decimal + frac : grouped;
    };
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (match, name) => {
      switch (name) {
        case 'amount_no_decimals':
          return delimit(value, 0, ',', '.');
        case 'amount_with_comma_separator':
          return delimit(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return delimit(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return delimit(value, 2, "'", '.');
        case 'amount_no_decimals_with_space_separator':
          return delimit(value, 0, ' ', '');
        case 'amount_with_space_separator':
          return delimit(value, 2, ' ', ',');
        case 'amount_with_period_and_space_separator':
          return delimit(value, 2, ' ', '.');
        default:
          return delimit(value, 2, ',', '.');
      }
    });
  }
  api.formatMoney = formatMoney;

  /* ---------- <dhrohar-wishlist-page> ---------- */
  const esc = (s) =>
    str(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  function sizedImage(src, width) {
    if (!src) return '';
    try {
      const url = new URL(src, root.location.href);
      url.searchParams.set('width', String(width));
      return url.href;
    } catch (e) {
      return src;
    }
  }

  const productCache = new Map();
  function fetchProduct(handle) {
    if (productCache.has(handle)) return productCache.get(handle);
    const base = (root.Shopify && root.Shopify.routes && root.Shopify.routes.root) || '/';
    const promise = fetch(`${base}products/${encodeURIComponent(handle)}.js`, { headers: { Accept: 'application/json' } })
      .then((response) => (response.ok ? response.json() : null))
      .catch(() => undefined); // undefined = network problem (keep saved details), null = product gone
    productCache.set(handle, promise);
    return promise;
  }

  class DhroharWishlistPage extends root.HTMLElement {
    connectedCallback() {
      this.list = this.querySelector('[data-wishlist-items]');
      this.empty = this.querySelector('[data-wishlist-empty]');
      this.countEl = this.querySelector('[data-wishlist-count-text]');
      this.render = this.render.bind(this);
      this.onClick = this.onClick.bind(this);
      this.addEventListener('click', this.onClick);
      doc.addEventListener(EVENT, this.render);
      this.render();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      doc.removeEventListener(EVENT, this.render);
    }

    money(cents) {
      return formatMoney(cents, this.dataset.moneyFormat);
    }

    async render(event) {
      try {
        // A removal from this page already took the line out; nothing else to redraw.
        if (event && event.detail && event.detail.action === 'remove' && this._removing) return;
        const items = api.items();
        const token = (this._token = (this._token || 0) + 1);
        this.toggleAttribute('data-empty', items.length === 0);
        if (this.empty) this.empty.hidden = items.length > 0;
        if (this.list) this.list.hidden = items.length === 0;
        this.updateCount(items.length);
        if (!items.length) {
          if (this.list) this.list.innerHTML = '';
          return;
        }
        if (this.list && !this.list.children.length) {
          this.list.innerHTML = items.map((item) => this.itemHTML(item, undefined, true)).join('');
        }
        const products = await Promise.all(items.map((item) => fetchProduct(item.handle)));
        if (token !== this._token || !this.list) return;
        this.list.innerHTML = items.map((item, i) => this.itemHTML(item, products[i])).join('');
      } catch (e) {
        /* keep whatever is on screen */
      }
    }

    updateCount(n) {
      if (!this.countEl) return;
      const note = this.dataset.browserNote || '';
      this.countEl.textContent = n ? `${n} saved${note ? '. ' + note : ''}` : '';
    }

    itemHTML(item, product, loading) {
      let variant = null;
      if (product && Array.isArray(product.variants)) {
        variant = product.variants.find((v) => String(v.id) === item.variantId) || null;
      }
      const gone = product === null || (product && !variant);
      const title = product ? product.title : item.title;
      const variantTitle = variant ? (variant.title === 'Default Title' ? '' : variant.title) : item.variantTitle;
      const url = product && !gone ? `${product.url || item.url}${variant ? `?variant=${variant.id}` : ''}` : item.url || '';
      const imageSrc =
        (variant && variant.featured_image && variant.featured_image.src) || (product && product.featured_image) || item.image;
      const image = sizedImage(imageSrc, 600);
      const available = variant ? variant.available : false;
      const price = variant ? this.money(variant.price) : '';
      const compare = variant && variant.compare_at_price > variant.price ? this.money(variant.compare_at_price) : '';
      // 0.6.0: exact saving from the same two figures (rounded down), as in snippets/price.liquid.
      const pct = compare ? Math.floor(((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price) : 0;
      const key = `${esc(item.handle)}|${esc(item.variantId)}`;

      let status = '';
      let action = '';
      if (loading || product === undefined) {
        action = '';
      } else if (gone) {
        status = `<p class="dhrohar-wl-item__status">${esc(this.dataset.goneText || 'This piece is no longer available.')}</p>`;
      } else if (!available) {
        status = `<p class="dhrohar-wl-item__status">Sold out</p>`;
        action = `<button type="button" class="button button--secondary dhrohar-wl-item__add" disabled>Sold out</button>`;
      } else {
        action = `<button type="button" class="button button--primary dhrohar-wl-item__add" data-wishlist-add="${esc(variant.id)}" aria-label="Add ${esc(title)}${variantTitle ? ` (${esc(variantTitle)})` : ''} to bag">${esc(this.dataset.addText || 'Add to bag')}</button>`;
      }
      const label = `${title}${variantTitle ? ` (${variantTitle})` : ''}`;
      const link = (inner, cls, hidden) =>
        url && !gone
          ? `<a href="${esc(url)}" class="${cls}"${hidden ? ' tabindex="-1" aria-hidden="true"' : ''}>${inner}</a>`
          : `<span class="${cls}">${inner}</span>`;

      return `<li class="dhrohar-wl-item${gone ? ' is-gone' : ''}" data-key="${key}">
        <div class="dhrohar-wl-item__media">
          ${link(image ? `<img src="${esc(image)}" alt="" loading="lazy" width="600" height="750">` : '', 'dhrohar-wl-item__media-link', true)}
        </div>
        <div class="dhrohar-wl-item__body">
          <h2 class="dhrohar-wl-item__title">${link(esc(title), 'dhrohar-wl-item__title-link')}</h2>
          ${variantTitle ? `<p class="dhrohar-wl-item__variant">${esc(variantTitle)}</p>` : ''}
          ${price ? `<p class="dhrohar-wl-item__price"><span>${esc(price)}</span>${compare ? ` <span class="dh-mrp"><span class="dh-mrp__label">MRP</span><span class="visually-hidden">Regular price</span> <s class="dhrohar-wl-item__compare">${esc(compare)}</s></span>${pct > 0 ? ` <span class="dh-mrp__off">(${pct}% off)</span>` : ''}` : ''}</p>` : ''}
          ${status}
          <p class="dhrohar-wl-item__message" role="status" data-wishlist-message></p>
          <div class="dhrohar-wl-item__actions">
            ${action}
            <button type="button" class="dhrohar-wl-item__remove" data-wishlist-remove data-handle="${esc(item.handle)}" data-variant-id="${esc(item.variantId)}" aria-label="Remove ${esc(label)} from wishlist">Remove</button>
          </div>
        </div>
      </li>`;
    }

    onClick(event) {
      const remove = event.target.closest('[data-wishlist-remove]');
      if (remove) {
        event.preventDefault();
        this.removeItem(remove);
        return;
      }
      const add = event.target.closest('[data-wishlist-add]');
      if (add) {
        event.preventDefault();
        this.addToBag(add);
      }
    }

    removeItem(button) {
      try {
        const line = button.closest('.dhrohar-wl-item');
        const next = line && (line.nextElementSibling || line.previousElementSibling);
        const title = line ? (line.querySelector('.dhrohar-wl-item__title') || {}).textContent || '' : '';
        this._removing = true;
        api.remove(button.dataset.handle, button.dataset.variantId);
        this._removing = false;
        if (line) line.remove();
        const left = api.count();
        this.updateCount(left);
        announce(`Removed from your wishlist: ${title.trim()}`);
        if (!left) {
          this.render();
          const heading = this.empty && this.empty.querySelector('h2, a');
          if (heading) {
            heading.setAttribute('tabindex', '-1');
            heading.focus();
          }
        } else if (next) {
          const target = next.querySelector('[data-wishlist-remove]');
          if (target) target.focus();
        }
      } catch (e) {
        this._removing = false;
      }
    }

    async addToBag(button) {
      const line = button.closest('.dhrohar-wl-item');
      const message = line && line.querySelector('[data-wishlist-message]');
      const original = button.textContent;
      const setMessage = (text) => {
        if (message) message.textContent = text;
      };
      try {
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        button.textContent = 'Adding…';
        setMessage('');

        const cart = doc.querySelector('cart-drawer') || doc.querySelector('cart-notification');
        const body = { id: Number(button.dataset.wishlistAdd), quantity: 1 };
        if (cart && typeof cart.getSectionsToRender === 'function') {
          body.sections = cart.getSectionsToRender().map((section) => section.id);
          body.sections_url = root.location.pathname;
          if (typeof cart.setActiveElement === 'function') cart.setActiveElement(button);
        } else {
          body.sections = ['cart-icon-bubble'];
        }
        const addUrl = `${(root.routes && root.routes.cart_add_url) || '/cart/add'}.js`;
        const response = await fetch(addUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify(body),
        });
        const state = await response.json();
        if (!response.ok || state.status) {
          setMessage(state.description || state.message || 'This could not be added to your bag. Please try again.');
          return;
        }
        const ps = dawnPubSub();
        if (ps && ps.publish && ps.events.cartUpdate) {
          try {
            ps.publish(ps.events.cartUpdate, { source: 'dhrohar-wishlist', productVariantId: String(body.id), cartData: state });
          } catch (e) {
            /* ignore */
          }
        }
        if (cart && typeof cart.renderContents === 'function') {
          if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
          cart.renderContents(state);
        } else {
          const bubble = doc.getElementById('cart-icon-bubble');
          const html = state.sections && state.sections['cart-icon-bubble'];
          if (bubble && html) {
            const parsed = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
            if (parsed) bubble.innerHTML = parsed.innerHTML;
          }
          const cartUrl = (root.routes && root.routes.cart_url) || '/cart';
          if (message) message.innerHTML = `Added to your bag. <a href="${esc(cartUrl)}">Go to bag</a>`;
        }
        announce('Added to your bag');
      } catch (e) {
        setMessage('This could not be added to your bag. Please try again.');
      } finally {
        button.disabled = false;
        button.removeAttribute('aria-busy');
        button.textContent = original;
      }
    }
  }

  try {
    if (!root.customElements.get('dhrohar-wishlist-button'))
      root.customElements.define('dhrohar-wishlist-button', DhroharWishlistButton);
    if (!root.customElements.get('dhrohar-wishlist-count'))
      root.customElements.define('dhrohar-wishlist-count', DhroharWishlistCount);
    if (!root.customElements.get('dhrohar-wishlist-page'))
      root.customElements.define('dhrohar-wishlist-page', DhroharWishlistPage);
  } catch (e) {
    /* already defined elsewhere */
  }
})();
